/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  AccountingAccountRestService,
  CostCenterRestService,
  SystemVarsAccountingJobs,
  SystemVarsRestService,
} from '@gh/builder-shared-data';
import { FinderDialogConfig } from '@gh/common-internal-shared';
import { DictionaryService } from '@gh/common-shared';
import { AutocompleteItem, ID, withDisplayAsNameAndDescription } from '@gh/common-shared-data';
import { FilteredDataQuery, getSlicedData } from '@gh/core-data';
import { TranslationService } from '@gh/core-mls';
import { orderByComparator } from '@gh/core-util';
import { combineLatest$ } from '@gh/rx';
import { map$ } from '@gh/rx/operators';
import { flatMap, groupBy, isEmpty } from 'lodash';
import { Observable } from 'rxjs';
import {
  accountComparator,
  AccountingAccount,
  AccountingAccountFilter,
  SystemVarsAccountingExpenseModel,
  SystemVarsAccountingJobsModel,
  SystemVarsAccountingReceivableModel,
} from './model/system-vars-accounting.model';

export interface AccountingAccountFinderDialogConfig extends FinderDialogConfig {
  accountClass?: string;
  exclude?: AccountingAccount[];
  tmpCredentials?: boolean;
}

@Injectable()
export class SystemVarsAccountingBusinessService {
  constructor(
    private restService: SystemVarsRestService,
    private costCenterRest: CostCenterRestService,
    private accountingAccountRest: AccountingAccountRestService,
    private translations: TranslationService,
    private dictionaries: DictionaryService,
  ) {
  }

  initReceivables(): any {
    return {
      originalReceivables: this.restService.getAccountingReceivables(),
    };
  }

  initExpense(): any {
    return {
      originalJobs: this.restService.getAccountingExpense(),
    };
  }

  initOverheads(): any {
    return {
      jobType: SystemVarsAccountingJobs.OVERHEADS,
      originalJobs: this.restService.getAccountingJobs(SystemVarsAccountingJobs.OVERHEADS),
    };
  }

  initAdvertise(): any {
    return {
      jobType: SystemVarsAccountingJobs.ADVERTISE,
      originalJobs: this.restService.getAccountingJobs(SystemVarsAccountingJobs.ADVERTISE),
    };
  }

  initWages(): any {
    return {
      jobType: SystemVarsAccountingJobs.WAGES,
      originalJobs: this.restService.getAccountingJobs(SystemVarsAccountingJobs.WAGES),
    };
  }

  initLiabilities(): any {
    return {
      jobType: SystemVarsAccountingJobs.LIABILITIES,
      originalJobs: this.restService.getAccountingJobs(SystemVarsAccountingJobs.LIABILITIES),
    };
  }

  updateAccountingReceivables(settings: SystemVarsAccountingReceivableModel):
    Observable<SystemVarsAccountingReceivableModel> {
    return this.restService.updateAccountingReceivables(settings);
  }

  modifyAccountingReceivables(settings: SystemVarsAccountingReceivableModel):
    Observable<SystemVarsAccountingReceivableModel> {
    return this.restService.modifyAccountingReceivables(settings);
  }

  updateAccountingJobs(type: SystemVarsAccountingJobs, settings: SystemVarsAccountingJobsModel):
    Observable<SystemVarsAccountingJobsModel> {
    return this.restService.updateAccountingJobs(type, settings);
  }

  updateAccountingExpense(settings: SystemVarsAccountingExpenseModel):
    Observable<SystemVarsAccountingExpenseModel> {
    return this.restService.updateAccountingExpense(settings);
  }

  findAll(filter: AccountingAccountFilter): Observable<AccountingAccount[]> {
    return combineLatest$(this.accountingAccountRest.findAll(filter), this.dictionaries.get('ACCOUNTS_CLASSES')).pipe(
      map$(([accounts, accountClassDict]) => {
        const accountByClass = groupBy(accounts, (account) => account.accountClass);
        return flatMap(accountClassDict.seq(), ({ id, name: localizedClassName }) => {
          const group = accountByClass[id];
          return isEmpty(group) ?
            []
            : [<any>{ groupTitle: true, name: localizedClassName }]
              .concat(orderByComparator(group, accountComparator));
        });
      }));
  }

  findCostCentersByName(query: FilteredDataQuery<string>, excludeCcIds: ID[]): Observable<AutocompleteItem[]> {
    return this.costCenterRest
      .findAllAutocompleteItemsAsDataSource({
        searchString: query.filter,
        showDeleted: false,
        excludeIds: excludeCcIds,
      }).select(query).pipe(
        map$(getSlicedData),
        map$((items) => items.map(withDisplayAsNameAndDescription)));
  }
}
